/**
 * Composant de vue détaillée pour afficher les informations complètes d'un objet touristique
 */

import {
  html,
  nothing,
  unsafeCSS,
  CSSResultGroup,
  PropertyValues,
  TemplateResult,
} from "lit";
import { customElement, property, state } from "lit/decorators.js";
import {
  ObjetTouristique,
  PeriodeOuverture,
  MoyenCommunication,
} from "../types/apidae.d";
import type { WidgetConfiguration } from "../types/widgets.d";
import { DisplayedFieldId } from "../types/widgets.d";
import { InformationBlock } from "./DetailView/InformationBlock";
import { InformationBlockListing } from "./DetailView/InformationBlockListing";
import { InformationBlockListingWColumns } from "./DetailView/InformationBlockListingWColumns";
import { InformationBlockDisclaimer } from "./DetailView/InformationBlockDisclaimer";
import { PrivateDataBlock } from "./DetailView/PrivateDataBlock";
import { Icon } from "./Icon";
import { TranslationService } from "../services/TranslationService";
import { WidgetLanguage, WidgetFieldsInfos } from "../types/widgets.d";
import detailViewStyles from "../styles/detail-view.css?inline";
import imageCopyrightStyles from "../styles/image-copyright.css?inline";
import { getDisplayedFieldData } from "../utils/displayedFieldUtils";
import { isSameCalendarDay } from "../utils/openingUtils";
import { renderImageCopyrightBadge } from "../utils/imageCopyrightUtils";
import {
  t,
  buildDetailOfferShareUrl,
  copyTextToClipboard,
  getImageCopyright,
} from "../utils/widgetsUtils";
import { getMultimediaTypeIconName } from "../config/multimediaTypeIcons";
import {
  renderAccessibilitySection,
  renderLocationAndContactSection,
  renderCyclistsAndPublicSection,
  renderEquipmentsSection,
  renderExtraDocumentationSection,
  renderItineraryAndTopoSection,
  renderLabelsSection,
  renderLongDescriptionSection,
  renderNewsAndOffersSection,
  renderOpeningSection,
  renderPaymentMethodsSection,
  renderPricingSection,
  renderReservationSection,
  renderRoomsAndAnimalsSection,
  renderCapabilitiesSection,
  renderSustainableAndKidsSection,
  renderAlertAndTipsSection,
  renderOtherDescriptifThematiseSection,
  renderMapSection,
  renderOpenSystemSection,
  renderEllohaSection,
  renderLinkedOffersSection,
  renderPrivateDataSection,
} from "./DetailView/DetailViewSections";
import { StyleService } from "../services/StyleService";
import { ApidaeVisualHost } from "./ApidaeVisualHost";
import { MapNavigationChooser } from "./DetailView/MapNavigationChooser";
import { getLocationDisplayData } from "../utils/displayedFieldUtils";

// MapWidget n'est pas importé ici pour éviter une dépendance circulaire
// (BaseWidget → DetailView → MapWidget → BaseWidget). Il est chargé à la demande
// quand l'objet a des coordonnées (voir updated()).

// Import nécessaire pour enregistrer les composants
InformationBlock;
InformationBlockListing;
InformationBlockListingWColumns;
InformationBlockDisclaimer;
PrivateDataBlock;
Icon;
MapNavigationChooser;

@customElement("apidae-detail-view")
export class DetailView extends ApidaeVisualHost {
  @property({ type: Object })
  item!: ObjetTouristique;

  @property({ type: String, attribute: "language" })
  language: WidgetLanguage = WidgetLanguage.FR;

  @property({ type: Array })
  displayedFields: WidgetFieldsInfos[] = [];

  /** Configuration du widget parent (optionnel), utilisée pour la carte en bas de page */
  @property({ type: Object, attribute: false })
  widgetConfig: WidgetConfiguration | null = null;

  @property({ type: String, attribute: false })
  widgetId: string = "";

  @property({ type: Function, attribute: false })
  fetchLinkedObjects?: (itemId: number) => Promise<ObjetTouristique[]>;

  @property({ type: Function, attribute: false })
  onLinkedItemClick?: (item: ObjetTouristique) => void;

  @property({ type: Boolean, attribute: false })
  canGoBackToPreviousDetail: boolean = false;

  @property({ type: Function, attribute: false })
  onBackToPreviousDetail?: () => void;

  @property({ type: Boolean, attribute: false })
  hideLinkedOffersSection: boolean = false;

  @state()
  currentOpeningPeriodIndex: number = 0;

  @state()
  currentPricingPeriodIndex: number = 0;

  @state()
  private photoGalleryView: boolean = false;

  /** Carte chargée à la demande pour éviter la dépendance circulaire avec BaseWidget (exposé au contexte des sections) */
  @state()
  mapWidgetLoaded: boolean = false;

  /** Index des illustrations en erreur de chargement (pour ne pas les réafficher) */
  @state()
  private failedImages: Set<number> = new Set();

  @state()
  linkedObjects: ObjetTouristique[] = [];

  @state()
  linkedObjectsLoading: boolean = false;

  @state()
  private shareLinkCopiedTick: number = 0;

  @state()
  private mapNavigationOpen: boolean = false;

  static styles: CSSResultGroup = [
    unsafeCSS(detailViewStyles),
    unsafeCSS(imageCopyrightStyles),
  ];

  updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);
    if (changedProperties.has("item")) {
      this.currentOpeningPeriodIndex = 0;
      this.currentPricingPeriodIndex = 0;
      this.failedImages = new Set();
      this.shareLinkCopiedTick = 0;
      this.mapNavigationOpen = false;
      this.scrollToTop();
    }
    if (
      changedProperties.has("item") ||
      changedProperties.has("displayedFields") ||
      changedProperties.has("hideLinkedOffersSection")
    ) {
      this.loadLinkedObjects();
    }
    const hasCoords =
      !!this.item?.localisation?.geolocalisation?.geoJson?.coordinates;
    if (hasCoords && !this.mapWidgetLoaded) {
      import("../widgets/MapWidget").then(() => {
        this.mapWidgetLoaded = true;
      });
    }

    if (changedProperties.has("widgetConfig")) {
      this.applyApidaeCustomStyles();
    }
  }

  private hasLinkedFieldEnabled(): boolean {
    return this.displayedFields.some(
      (field) => field.id === DisplayedFieldId.OBJETS_LIES,
    );
  }

  private hasLiensInCurrentItem(): boolean {
    const liens = (this.item as any)?.liens?.liensObjetsTouristiquesTypes ?? [];
    return Array.isArray(liens) && liens.length > 0;
  }

  private async loadLinkedObjects(): Promise<void> {
    if (
      this.hideLinkedOffersSection ||
      !this.item?.id ||
      !this.fetchLinkedObjects ||
      !this.hasLinkedFieldEnabled() ||
      !this.hasLiensInCurrentItem()
    ) {
      this.linkedObjects = [];
      this.linkedObjectsLoading = false;
      return;
    }

    this.linkedObjectsLoading = true;
    try {
      const items = await this.fetchLinkedObjects(this.item.id);
      this.linkedObjects = (items || []).filter(
        (it) => it?.id !== this.item.id,
      );
    } catch (error) {
      console.error("Erreur chargement objets liés:", error);
      this.linkedObjects = [];
    } finally {
      this.linkedObjectsLoading = false;
    }
  }

  private scrollToTop(): void {
    requestAnimationFrame(() => {
      const overlay = this.renderRoot?.querySelector(".detail-overlay");
      if (overlay instanceof HTMLElement) {
        overlay.scrollTo({ top: 0, behavior: "auto" });
      }
    });
  }

  firstUpdated(changedProperties: PropertyValues) {
    super.firstUpdated(changedProperties);
    this.applyApidaeCustomStyles();
  }

  /** Personnalisation depuis le widget parent (`widgetConfig`), pas la prop héritée `visualCustomization`. */
  protected override applyApidaeCustomStyles(): void {
    StyleService.applyVisualCustomizationToShadowRoot(
      this.shadowRoot,
      this.widgetConfig?.visualCustomization ?? undefined,
    );
  }

  setCurrentOpeningPeriodIndex(index: number): void {
    this.currentOpeningPeriodIndex = Math.max(0, index);
    this.requestUpdate();
  }

  setCurrentPricingPeriodIndex(index: number): void {
    this.currentPricingPeriodIndex = Math.max(0, index);
    this.requestUpdate();
  }

  /** Fait défiler la fiche jusqu'à la section carte (conteneur .detail-overlay) avec animation */
  scrollToMapSection(): void {
    const section = this.renderRoot?.querySelector("#detail-map-section");
    const overlay = this.renderRoot?.querySelector(".detail-overlay");
    if (section && overlay) {
      const sectionTopInViewport = section.getBoundingClientRect().top;
      const overlayTopInViewport = overlay.getBoundingClientRect().top;
      const scrollTarget =
        overlay.scrollTop + sectionTopInViewport - overlayTopInViewport - 20;
      overlay.scrollTo({ top: Math.max(0, scrollTarget), behavior: "smooth" });
    } else {
      section?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  openMapNavigationChooser(): void {
    this.mapNavigationOpen = true;
  }

  closeMapNavigationChooser(): void {
    this.mapNavigationOpen = false;
  }

  private handleClose() {
    this.dispatchEvent(
      new CustomEvent("close", { bubbles: true, composed: true }),
    );
  }

  private handleShareFeedbackAnimationEnd(e: AnimationEvent): void {
    if (e.animationName !== "detail-share-feedback-lifecycle") return;
    this.shareLinkCopiedTick = 0;
  }

  private async handleShareLink(): Promise<void> {
    if (!this.item?.id) return;

    const url = buildDetailOfferShareUrl(this.item.id);
    await copyTextToClipboard(url);

    if (this.shareLinkCopiedTick > 0) {
      this.shareLinkCopiedTick = 0;
      await this.updateComplete;
    }
    this.shareLinkCopiedTick += 1;
  }

  private handleBackdropClick(e: Event) {
    if (e.target === e.currentTarget) {
      this.handleClose();
    }
  }

  private _boundKeydown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      if (this.mapNavigationOpen) {
        this.closeMapNavigationChooser();
        return;
      }
      this.handleClose();
    }
  };

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener("keydown", this._boundKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this._boundKeydown);
    this.shareLinkCopiedTick = 0;
    this.mapNavigationOpen = false;
    super.disconnectedCallback();
  }

  // Méthodes utilitaires inspirées de ListWidget
  getLabel(libelle: any): string {
    return TranslationService.getLabelWithFallback(libelle, this.language);
  }

  formatDate(dateStr: string, options?: Intl.DateTimeFormatOptions): string {
    return TranslationService.formatDate(dateStr, this.language, options);
  }

  getCoordonneesValue(coordonnees: {
    fr?: string;
    en?: string;
    es?: string;
    it?: string;
    de?: string;
    nl?: string;
    ru?: string;
    zh?: string;
    ptBR?: string;
    ja?: string;
    ko?: string;
  }): string | null {
    if (!coordonnees) return null;

    const langMap: Record<string, keyof typeof coordonnees> = {
      [WidgetLanguage.FR]: "fr",
      [WidgetLanguage.EN]: "en",
      [WidgetLanguage.ES]: "es",
      [WidgetLanguage.IT]: "it",
      [WidgetLanguage.DE]: "de",
      [WidgetLanguage.NL]: "nl",
      [WidgetLanguage.RU]: "ru",
      [WidgetLanguage.ZH]: "zh",
      [WidgetLanguage.PT_BR]: "ptBR",
      [WidgetLanguage.JA]: "ja",
      [WidgetLanguage.KO]: "ko",
    };

    const key = langMap[this.language];
    if (key && coordonnees[key]) {
      return coordonnees[key] || null;
    }

    if (coordonnees.fr) {
      return coordonnees.fr;
    }

    const values = Object.values(coordonnees).filter((v) => v);
    return values.length > 0 ? values[0] : null;
  }

  formatOuverturePeriod(periode: PeriodeOuverture): string | null {
    if (!periode.dateDebut) return null;

    const dateDebut = this.formatDate(periode.dateDebut, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    if (periode.dateFin) {
      const dateFin = this.formatDate(periode.dateFin, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
      if (isSameCalendarDay(periode.dateDebut, periode.dateFin)) {
        return `Le ${dateDebut}`;
      }
      return `Du ${dateDebut} au ${dateFin}`;
    }
    return `Du ${dateDebut}`;
  }

  getTelephones(): MoyenCommunication[] {
    return (
      this.item.informations?.moyensCommunication?.filter(
        (m) => m.type?.id === 201,
      ) || []
    );
  }

  getEmails(): MoyenCommunication[] {
    return (
      this.item.informations?.moyensCommunication?.filter(
        (m) => m.type?.id === 204,
      ) || []
    );
  }

  getWebsites(): MoyenCommunication[] {
    return (
      this.item.informations?.moyensCommunication?.filter(
        (m) => m.type?.id === 205,
      ) || []
    );
  }

  getReservationUrl(): string | null {
    const website = this.getWebsites()[0];
    if (!website) return null;
    return this.getCoordonneesValue(website.coordonnees);
  }

  /**
   * Vérifie si un champ doit être affiché selon la configuration
   */
  shouldDisplayField(fieldId: string): boolean {
    if (!this.item) return false;

    const fieldConfig = this.displayedFields
      ? this.displayedFields.find((field) => field.id === fieldId)
      : undefined;

    // N'afficher que si le DisplayedFieldId est dans la config et qu'il y a des données
    const hasConfig = !!this.displayedFields && this.displayedFields.length > 0;
    if (!hasConfig || !fieldConfig) {
      return false;
    }

    const data = getDisplayedFieldData(this.item, fieldId, {
      language: this.language,
      getLabel: (libelle) => this.getLabel(libelle),
      formatDate: (dateStr, options) => this.formatDate(dateStr, options),
      t: (key) => t(key, this.language),
      fieldConfig,
    });

    return data !== null;
  }

  /**
   * Retourne l'icône appropriée selon le type de multimédia (API Apidae / Sitra).
   */
  getMultimediaIcon(type: string): string {
    return getMultimediaTypeIconName(type);
  }

  private formatJourSemaine(jour: string): string {
    const jours: Record<string, string> = {
      LUNDI: "Lundi",
      MARDI: "Mardi",
      MERCREDI: "Mercredi",
      JEUDI: "Jeudi",
      VENDREDI: "Vendredi",
      SAMEDI: "Samedi",
      DIMANCHE: "Dimanche",
    };
    return jours[jour] || jour;
  }

  getOuvertureItems(periode: PeriodeOuverture): Array<{
    icon: string;
    title: string;
    content: string;
  }> {
    const items: Array<{ icon: string; title: string; content: string }> = [];

    // Si complementHoraire existe, l'utiliser
    if (periode.complementHoraire) {
      const horaire = this.getLabel(periode.complementHoraire);
      // Essayer de parser les jours depuis ouverturesJournalieres
      if ((periode as any).ouverturesJournalieres) {
        const jours = (periode as any).ouverturesJournalieres.map((oj: any) =>
          this.formatJourSemaine(oj.jour),
        );
        if (jours.length > 0) {
          jours.forEach((jour: string) => {
            items.push({
              icon: "bold/time/clock_square",
              title: jour,
              content: horaire,
            });
          });
        } else {
          items.push({
            icon: "bold/time/clock_square",
            title: "Horaires",
            content: horaire,
          });
        }
      } else {
        items.push({
          icon: "bold/time/clock_square",
          title: "Horaires",
          content: horaire,
        });
      }
    } else if ((periode as any).ouverturesJournalieres) {
      // Si pas de complementHoraire mais des ouverturesJournalieres
      const jours = (periode as any).ouverturesJournalieres.map((oj: any) =>
        this.formatJourSemaine(oj.jour),
      );
      jours.forEach((jour: string) => {
        items.push({
          icon: "bold/time/clock_square",
          title: jour,
          content: "Sur réservation",
        });
      });
    }

    return items;
  }

  getPeriodesGroupedByYear(): Map<number, PeriodeOuverture[]> {
    const periodesByYear = new Map<number, PeriodeOuverture[]>();

    if (!this.item.ouverture?.periodesOuvertures) {
      return periodesByYear;
    }

    this.item.ouverture.periodesOuvertures.forEach((periode) => {
      if (!periode.dateDebut) return;

      const dateDebut = new Date(periode.dateDebut);
      const year = dateDebut.getFullYear();

      // Si tousLesAns est true, ajouter pour les années courante et suivante
      const periodeAny = periode as any;
      if (periodeAny.tousLesAns) {
        const currentYear = new Date().getFullYear();
        for (let y = currentYear; y <= currentYear + 1; y++) {
          if (!periodesByYear.has(y)) {
            periodesByYear.set(y, []);
          }
          periodesByYear.get(y)!.push(periode);
        }
      } else {
        if (!periodesByYear.has(year)) {
          periodesByYear.set(year, []);
        }
        periodesByYear.get(year)!.push(periode);
      }
    });

    return periodesByYear;
  }

  /** Retourne les images depuis le case DisplayedFieldId.IMAGES (comme les autres sections), en excluant celles en erreur de chargement. */
  private getHeaderImageItems(): Array<{
    url: string;
    index: number;
    copyright: string;
  }> {
    if (!this.item) return [];
    const fieldConfig = this.displayedFields?.find(
      (f) => f.id === DisplayedFieldId.IMAGES,
    );
    const data = getDisplayedFieldData(this.item, DisplayedFieldId.IMAGES, {
      language: this.language,
      getLabel: (libelle) => this.getLabel(libelle),
      formatDate: (dateStr, options) => this.formatDate(dateStr, options),
      t: (key) => t(key, this.language),
      fieldConfig,
    });
    if (!data?.items?.length) return [];
    return (data.items as Array<{ url?: string | null; copyright?: string }>)
      .map((item, idx) => ({
        url: item.url ?? null,
        index: idx,
        copyright: (item.copyright ?? "").trim(),
      }))
      .filter(
        (i): i is { url: string; index: number; copyright: string } =>
          !!i.url && !this.failedImages.has(i.index),
      );
  }

  private renderGalleryOverlays(
    copyright: string,
    extra?: TemplateResult | typeof nothing,
  ) {
    const badge = renderImageCopyrightBadge(copyright);
    if (badge === nothing && (extra === undefined || extra === nothing)) {
      return nothing;
    }
    return html`
      <div class="image-overlays image-overlays--bottom-right">
        ${badge}${extra ?? nothing}
      </div>
    `;
  }

  private renderGalleryImage(
    item: { url: string; index: number; copyright: string },
    cellClass: string,
    extraOverlays?: TemplateResult | typeof nothing,
  ) {
    return html`
      <div class="${cellClass}">
        <img
          class="detail-image"
          src="${item.url}"
          alt=""
          loading="lazy"
          @error=${() => this.handleHeaderImageError(item.index)}
        />
        ${this.renderGalleryOverlays(item.copyright, extraOverlays)}
      </div>
    `;
  }

  private handleHeaderImageError(index: number) {
    this.failedImages = new Set(this.failedImages).add(index);
    this.requestUpdate();
  }

  private openPhotoGallery() {
    this.photoGalleryView = true;
  }

  private closePhotoGallery() {
    this.photoGalleryView = false;
  }

  private renderPhotoGalleryView(
    imageItems: Array<{ url: string; index: number; copyright: string }>,
  ) {
    const pattern: number[] = [1, 2, 1];
    let index = 0;
    const gridRows: { indices: number[]; height: string }[] = [];
    while (index < imageItems.length) {
      for (let p = 0; p < pattern.length && index < imageItems.length; p++) {
        const n = pattern[p];
        const indices = [];
        for (let j = 0; j < n && index < imageItems.length; j++)
          indices.push(index++);
        const isFirstRowOfTriple = p === 0;
        gridRows.push({
          indices,
          height:
            indices.length === 1 && isFirstRowOfTriple ? "600px" : "275px",
        });
      }
    }
    return html`
      <div class="detail-photo-visit">
        <h2 class="detail-photo-visit__title">
          ${t("photoVisit", this.language)}
        </h2>
        <div class="detail-photo-visit__grid">
          ${gridRows.map(
            (row) => html`
              <div
                class="detail-photo-visit__row detail-photo-visit__row--${row
                  .indices.length}"
                style="height: ${row.height}"
              >
                ${row.indices.map(
                  (i) => html`
                    <div class="detail-photo-visit__cell">
                      <img
                        class="detail-photo-visit__img"
                        src="${imageItems[i].url}"
                        alt=""
                        loading="lazy"
                        @error=${() =>
                          this.handleHeaderImageError(imageItems[i].index)}
                      />
                      ${renderImageCopyrightBadge(imageItems[i].copyright, {
                        corner: true,
                      })}
                    </div>
                  `,
                )}
              </div>
            `,
          )}
        </div>
        <apidae-button
          variant="secondary100"
          class="detail-photo-visit__back"
          .visualCustomization=${this.widgetConfig?.visualCustomization ?? null}
          @click=${this.closePhotoGallery}
        >
          <apidae-icon
            name="bold/arrows/round_arrow_left"
            size="22"
            color="currentColor"
            .visualCustomization=${this.widgetConfig?.visualCustomization ??
            null}
          ></apidae-icon>
          <span style="margin-left: 8px"
            >${t("backToFiche", this.language)}</span
          >
        </apidae-button>
      </div>
    `;
  }

  render() {
    const allImageItems = this.getHeaderImageItems();
    // Exception images : sans DisplayedFieldId.IMAGES on n'affiche que la première image
    const imageItems = this.shouldDisplayField(DisplayedFieldId.IMAGES)
      ? allImageItems
      : allImageItems.slice(0, 1);
    const hasImages = imageItems.length > 0;
    const showSeeMore = imageItems.length > 3;

    const detailVisual = this.widgetConfig?.visualCustomization ?? null;
    const locationForMapNavigation = getLocationDisplayData(this.item, {
      getLabel: (libelle) => this.getLabel(libelle),
    });
    const mapNavigationCoordinates =
      this.item?.localisation?.geolocalisation?.geoJson?.coordinates ?? null;
    return html`
      <div
        class="detail-overlay"
        @click=${this.handleBackdropClick}
        role="presentation"
      >
        <div class="detail-close-wrapper">
          <div class="detail-header-actions">
            <div class="detail-share-action">
              ${this.shareLinkCopiedTick > 0
                ? html`
                    <div
                      class="detail-share-feedback"
                      role="status"
                      @animationend=${this.handleShareFeedbackAnimationEnd}
                    >
                      <apidae-icon
                        class="detail-share-feedback__icon"
                        name="bold/essentional_ui/check_square"
                        size="20"
                        color="#3d9a4a"
                        .visualCustomization=${detailVisual}
                      ></apidae-icon>
                      <span>${t("linkCopied", this.language)}</span>
                    </div>
                  `
                : ""}
              <button
                class="detail-action-button share-button"
                @click=${this.handleShareLink}
                title="${t("shareLink", this.language)}"
                aria-label="${t("shareLink", this.language)}"
              >
                <apidae-icon
                  name="bold/text_formatting/link_minimalistic_2"
                  size="22"
                  color="currentColor"
                  .visualCustomization=${detailVisual}
                ></apidae-icon>
              </button>
            </div>
            <button
              class="detail-action-button close-button"
              @click=${this.handleClose}
              title="Fermer"
              aria-label="Fermer"
            >
              ✕
            </button>
          </div>
        </div>

        <div
          class="detail-container"
          @click=${(e: Event) => e.stopPropagation()}
        >
          <div class="detail-slider">
            <div
              class="detail-pane detail-pane--detail"
              ?data-active=${!this.photoGalleryView}
            >
              <div class="detail-header-block">
                <div
                  class="detail-header detail-header-gallery detail-header-gallery--${hasImages
                    ? Math.min(imageItems.length, 3)
                    : 1}"
                >
                  ${!hasImages
                    ? html`<div class="detail-header-gallery__single">
                        <div
                          class="detail-image-placeholder"
                          aria-hidden="true"
                        >
                          ${(this.getLabel(this.item?.nom) || "")
                            .charAt(0)
                            .toUpperCase() || ""}
                        </div>
                        ${this.renderGalleryOverlays(
                          getImageCopyright(this.item, this.language),
                        )}
                      </div>`
                    : imageItems.length === 1
                      ? this.renderGalleryImage(
                          imageItems[0],
                          "detail-header-gallery__single",
                        )
                      : imageItems.length === 2
                        ? html`
                            ${this.renderGalleryImage(
                              imageItems[0],
                              "detail-header-gallery__left",
                            )}
                            ${this.renderGalleryImage(
                              imageItems[1],
                              "detail-header-gallery__right detail-header-gallery__right--single",
                            )}
                          `
                        : html`
                            ${this.renderGalleryImage(
                              imageItems[0],
                              "detail-header-gallery__left",
                            )}
                            <div class="detail-header-gallery__right">
                              ${this.renderGalleryImage(
                                imageItems[1],
                                "detail-header-gallery__right-top",
                              )}
                              ${this.renderGalleryImage(
                                imageItems[2],
                                "detail-header-gallery__right-bottom",
                                showSeeMore
                                  ? html`
                                      <apidae-button
                                        variant="secondary100"
                                        class="detail-header-gallery__see-more"
                                        .visualCustomization=${detailVisual}
                                        @click=${this.openPhotoGallery}
                                        aria-label="${t(
                                          "showMore",
                                          this.language,
                                        )}"
                                      >
                                        ${t("showMore", this.language)}
                                        <apidae-icon
                                          name="bold/arrows/round_arrow_right"
                                          size="22"
                                          color="currentColor"
                                          slot="icon"
                                          .visualCustomization=${detailVisual}
                                        ></apidae-icon>
                                      </apidae-button>
                                    `
                                  : nothing,
                              )}
                            </div>
                          `}
                </div>
                <h1 class="detail-header-title">
                  ${this.getLabel(this.item?.nom) || ""}
                </h1>
              </div>

              <div class="detail-content detail-content--slide">
                ${renderLocationAndContactSection(this)}
                ${renderOpeningSection(this)}
                ${renderLongDescriptionSection(this)}
                ${renderPricingSection(this)}
                ${renderPaymentMethodsSection(this)}
                ${renderReservationSection(this)}
                ${renderExtraDocumentationSection(this)}
                ${renderEquipmentsSection(this)} ${renderLabelsSection(this)}
                ${renderAccessibilitySection(this)}
                ${renderRoomsAndAnimalsSection(this)}
                ${renderCapabilitiesSection(this)}
                ${renderAlertAndTipsSection(this)}
                ${renderItineraryAndTopoSection(this)}
                ${renderSustainableAndKidsSection(this)}
                ${renderPrivateDataSection(this)}
                ${renderNewsAndOffersSection(this)}
                ${renderCyclistsAndPublicSection(this)}
                ${renderOtherDescriptifThematiseSection(this)}
                ${renderLinkedOffersSection(this)} ${renderMapSection(this)}
                ${renderOpenSystemSection(this)} ${renderEllohaSection(this)}
                ${this.hideLinkedOffersSection && this.canGoBackToPreviousDetail
                  ? html`
                      <apidae-button
                        variant="secondary100"
                        class="detail-linked-back-button"
                        .visualCustomization=${detailVisual}
                        @click=${() => this.onBackToPreviousDetail?.()}
                      >
                        <apidae-icon
                          name="bold/arrows/round_arrow_left"
                          size="22"
                          color="currentColor"
                          .visualCustomization=${detailVisual}
                        ></apidae-icon>
                        <span style="margin-left: 8px"
                          >${t("backToFiche", this.language)}</span
                        >
                      </apidae-button>
                    `
                  : ""}
              </div>
            </div>
            <div
              class="detail-pane detail-pane--photo"
              ?data-active=${this.photoGalleryView}
            >
              ${this.renderPhotoGalleryView(imageItems)}
            </div>
          </div>
        </div>
        ${this.mapNavigationOpen && locationForMapNavigation
          ? html`
              <apidae-map-navigation-chooser
                .language=${this.language}
                .location=${locationForMapNavigation}
                .coordinates=${mapNavigationCoordinates}
                .visualCustomization=${detailVisual}
                .onClose=${() => this.closeMapNavigationChooser()}
              ></apidae-map-navigation-chooser>
            `
          : ""}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "apidae-detail-view": DetailView;
  }
}
